import { useCallback, useSyncExternalStore } from "react";
import { parseVoicePreferences, type VoicePreferences } from "./voiceLibrary";

const changedEvent = "afterform-voice-preferences";
const sessionFallback = new Map<string, string>();
function subscribe(listener: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key) sessionFallback.delete(event.key); else sessionFallback.clear();
    listener();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(changedEvent, listener);
  return () => { window.removeEventListener("storage", onStorage); window.removeEventListener(changedEvent, listener); };
}
function read(key: string) {
  try { return sessionFallback.get(key) ?? localStorage.getItem(key) ?? null; }
  catch { return sessionFallback.get(key) ?? null; }
}
/** Browser-local voice likes and selection history, isolated by workspace. */
export function useVoicePreferences(spaceID: string) {
  const key = `afterform:voice-library:${spaceID}`;
  const snapshot = useCallback(() => read(key), [key]);
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const preferences = parseVoicePreferences(raw);
  function update(change: (current: VoicePreferences) => VoicePreferences) {
    const next = JSON.stringify(change(parseVoicePreferences(read(key))));
    try { localStorage.setItem(key, next); sessionFallback.delete(key); }
    catch { sessionFallback.set(key, next); }
    window.dispatchEvent(new Event(changedEvent));
  }
  return { preferences,
    toggleLike: (voice: string) => update(current => ({ ...current, liked: current.liked.includes(voice) ? current.liked.filter(key => key !== voice) : [...current.liked, voice] })),
    markUsed: (voice: string) => update(current => ({ ...current, recent: { ...current.recent, [voice]: Date.now() } })),
  };
}
